import { useFormRuntimeRegistration } from './useFormRuntimeRegistration.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import { FormFieldMessagesAction } from '../../forms-core/state/formfield-messages-state-reducer.js';
import {
  ValidationError,
  type ValidationState,
} from '../../validation/types.js';
import type { FormControlValidator } from '../form.types.js';

/**
 * Options for `useFormRegistration`.
 * @internal
 */
export interface FormRegistrationOptions<TValue = unknown> {
  /** Logical form-control id — matches the input's `id`. */
  id: string;
  /** Submit key; the form snapshot keys values by `name ?? id`. */
  name?: string;
  /**
   * Associates with a `<Form>` by id when the control is rendered outside the
   * form's React subtree (mirrors the native `form="…"` attribute).
   */
  form?: string;
  /**
   * The underlying input element, reserved for a11y / focus wiring. Accepted
   * now so the contract is stable; the runtime does not yet consume it — the
   * focus/blur wiring that reads it lands with APPDEV-17909.
   */
  inputElement?: HTMLElement | null;
  /** Focuses the control on error (focus-first-invalid). */
  focus: () => void;
  /** Reads the control's current value. */
  getValue: () => TValue;
  /** Optional per-control validator. */
  validate?: FormControlValidator<TValue>;
}

/**
 * Registers a custom (non-Strato) control with the enclosing `<Form>` so it
 * participates in submit, validation, snapshot, and focus-first-invalid. Strato
 * controls do not call this directly — they register through `FormField`.
 *
 * Returns a `reportValidity` for controls that want to push live validity
 * between submits; ignoring it is fine — the form pulls each control on submit.
 * @internal
 */
export function useFormRegistration<TValue = unknown>(
  options: FormRegistrationOptions<TValue>,
): (state: ValidationState) => void {
  const { id, name, form, focus, getValue, validate } = options;
  const { dispatch } = useFormFieldMessagesContext();

  const reportValidity = useFormRuntimeRegistration({
    id,
    name,
    form,
    getValue,
    focus,
    validateField: async ({ reveal, trigger }) => {
      const result = await validate?.(getValue());
      if (reveal) {
        dispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
      return {
        status: 'done',
        trigger,
        errors: toValidationErrors(result, id),
      };
    },
    resetValidation: () => {
      // Consumer-owned controls only reset touched state here; unlike the Strato
      // path, native `customValidity` clearing is intentionally left to the
      // consumer's own validator, which owns that element's validity.
      dispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: false, resetShowError: true },
      });
    },
  });

  return reportValidity ?? (() => undefined);
}

/** Maps a per-control validator result to the canonical error list. @internal */
function toValidationErrors(
  result: Awaited<ReturnType<FormControlValidator>> | undefined,
  id: string,
): ValidationError[] {
  if (!result) {
    return [];
  }
  if (typeof result === 'string') {
    return [
      new ValidationError(result, { source: 'custom', formControlId: id }),
    ];
  }
  return result.map(
    (error) =>
      new ValidationError(error.message, {
        source: 'custom',
        validityStateKey: error.validityStateKey,
        formControlId: id,
        cause: error.cause,
      }),
  );
}
